import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { marked } from 'marked';
import { saveAs } from 'file-saver';
import { BackendService } from '../../services/general/backend.service';

@Component({
    selector: 'app-nerf-simulator',
    templateUrl: './nerf-simulator.component.html',
    styleUrls: ['./nerf-simulator.component.scss'],
    standalone: false
})
export class NerfSimulatorComponent implements OnInit, OnDestroy {
  @Input() projectId: any;

  public simulatorForm: FormGroup;
  public loading = false;
  public simulationResult: any = null;
  public hasResult = false;
  public simulationHistory: any[] = [];
  public loadingHistory = false;
  public savingSimulation = false;
  public currentSimulationId: number | null = null;
  public currentSimulationCreatedAt: string | null = null;
  public isSimulationSaved = false;

  /** Centri di riferimento caricati da nerf-simulator-records (combo in cima al form). */
  public cityRecords: any[] = [];
  public loadingCities = false;
  /** Combo selezione centro: nessuna selezione => form azzerato e disabilitato. */
  public cityControl = new FormControl<number | null>(null);
  /** Centri filtrati dalla ricerca testuale dentro la combo. */
  public filteredCityRecords: any[] = [];
  /** Input di ricerca dentro il pannello della combo (per l'autofocus all'apertura). */
  @ViewChild('citySearchInput') citySearchInput?: ElementRef<HTMLInputElement>;

  /** Colonne tabella storico (mat-table + table-primary come nel resto dell'app) */
  readonly historyDisplayedColumns: string[] = [
    'select',
    'created_at',
    'city',
    'utilization_percent',
    'patients_per_month',
      'actions',
  ];

  /** Id delle simulazioni selezionate per la generazione del report. */
  public selectedSimulationIds = new Set<number>();
  /** Se true la tabella storico mostra tutte le simulazioni, altrimenti solo quelle del centro selezionato. */
  public showAllSimulations = false;
  /** Report in corso di generazione (spinner sul pulsante). */
  public generatingReport = false;
  /** Ultimo report generato (status/risultato mostrati sotto la tabella). */
  public currentReport: any = null;
  private reportPollTimer: any = null;

  /**
   * Valori di default solo per i campi modificabili in UI; i fissi/disabilitati restano quelli attuali (raw).
   * Campi con markup commentato nel template (nascosti all’utente ma ancora nel FormGroup):
   * Avg_Infusion_Min_Scenario, Gap_TimeHours_Per_Day, Hours_Per_Day, Working_Days_Month, avg_patients_per_chair_per_day.
   */
  private readonly editableParamDefaults: Record<string, number> = {
    ocrelizumab_subcutaneous_composition: 40,
    ocrelizumab_intravenous_composition: 40,
    ublituximab_composition: 20,
    natalizumab_intravenous_composition: 0,
    natalizumab_subcutaneous_composition: 0,
    Gap_Min: 15,
    Safety_Buffer: 10,
    No_Show_Rate: 0,
    Nurses_per_Shift: 3,
    Patients_per_Nurse: 2,
    Chairs_Active: 5,
    number_of_opening_days: 20,
    infusion_service_hours: 8,

    // Cost parameters (editable)
    ocrelizumab_intravenous_cost_single_infusion: 2000,
    ocrelizumab_subcutaneous_cost_single_infusion: 3238,
    ublituximab_cost_single_infusion: 1800,
    natalizumab_intravenous_cost_single_infusion: 2000,
    natalizumab_subcutaneous_cost_single_infusion: 2000,
    materials_cost_single_infusion: 20,
    nurses_cost_for_hour: 0,
  };

  constructor(
    private fb: FormBuilder,
    private backend: BackendService
  ) {
    this.simulatorForm = this.fb.group({
      // NB: non rimuovere i controlli il cui input è commentato in .html — servono ancora al backend (getRawValue).
      // Medication parameters (compositions stored as percentages for display)
      ocrelizumab_subcutaneous_min: [10, [Validators.required, Validators.min(0)]],
      ocrelizumab_subcutaneous_composition: [40, [Validators.required, Validators.min(0), Validators.max(100)]],
      ocrelizumab_intravenous_min: [180, [Validators.required, Validators.min(0)]],
      ocrelizumab_intravenous_composition: [40, [Validators.required, Validators.min(0), Validators.max(100)]],
      ublituximab_min: [60, [Validators.required, Validators.min(0)]],
      ublituximab_composition: [20, [Validators.required, Validators.min(0), Validators.max(100)]],
      natalizumab_intravenous_min: [30, [Validators.required, Validators.min(0)]],
      natalizumab_subcutaneous_min: [30, [Validators.required, Validators.min(0)]],
      natalizumab_intravenous_composition: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      natalizumab_subcutaneous_composition: [0, [Validators.required, Validators.min(0), Validators.max(100)]],

      // Working hours parameters
      Hours_Per_Day: [8, [Validators.required, Validators.min(0)]],
      Gap_Min: [15, [Validators.required, Validators.min(0)]],
      Working_Days_Month: [20, [Validators.required, Validators.min(0)]],
      Avg_Infusion_Min_Scenario: [156, [Validators.required, Validators.min(0)]],
      Safety_Buffer: [10, [Validators.required, Validators.min(0), Validators.max(100)]],
      No_Show_Rate: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      Gap_TimeHours_Per_Day: [2, [Validators.required, Validators.min(0)]],

      // Staff parameters
      Nurses_per_Shift: [3, [Validators.required, Validators.min(0)]],
      Patients_per_Nurse: [2, [Validators.required, Validators.min(0)]],

      // Capacity parameters
      avg_patients_per_chair_per_day: [2.470149, [Validators.required, Validators.min(0)]],
      Chairs_Active: [5, [Validators.required, Validators.min(0)]],
      number_of_opening_days: [20, [Validators.required, Validators.min(0)]],

      // Time parameters
      prep_time_chair_materials: [20, [Validators.required, Validators.min(0)]],
      patient_reception_assessment_time: [26, [Validators.required, Validators.min(0)]],
      vitals_premedication_setup_time: [28, [Validators.required, Validators.min(0)]],
      // Fixed per-drug post-infusion monitoring times (minutes). Read-only / not user-editable.
      post_infusion_surveillance_time_ocrelizumab_subcutaneous: [52, [Validators.min(0)]],
      post_infusion_surveillance_time_ocrelizumab_intravenous: [52, [Validators.min(0)]],
      post_infusion_surveillance_time_ublituximab: [52, [Validators.min(0)]],
      post_infusion_surveillance_time_natalizumab_intravenous: [60, [Validators.min(0)]],
      post_infusion_surveillance_time_natalizumab_subcutaneous: [60, [Validators.min(0)]],
      post_infusion_admin_time: [17, [Validators.required, Validators.min(0)]],
      avg_operational_gap_between_infusions: [19, [Validators.required, Validators.min(0)]],
      infusion_service_hours: [8, [Validators.required, Validators.min(0)]],

      // Cost parameters
      ocrelizumab_intravenous_cost_single_infusion: [2000, [Validators.required, Validators.min(0)]],
      ocrelizumab_subcutaneous_cost_single_infusion: [3238, [Validators.required, Validators.min(0)]],
      ublituximab_cost_single_infusion: [1800, [Validators.required, Validators.min(0)]],
      natalizumab_intravenous_cost_single_infusion: [2000, [Validators.required, Validators.min(0)]],
      natalizumab_subcutaneous_cost_single_infusion: [2000, [Validators.required, Validators.min(0)]],
      materials_cost_single_infusion: [20, [Validators.required, Validators.min(0)]],
      nurses_cost_for_hour: [0, [Validators.required, Validators.min(0)]],
    }, { validators: this.compositionSumValidator });
  }

  // Custom validator to ensure the sum of compositions is <= 100%
  private compositionSumValidator = (control: AbstractControl): ValidationErrors | null => {
    const rituximab = Number(control.get('ocrelizumab_subcutaneous_composition')?.value) || 0;
    const ocrelizumab = Number(control.get('ocrelizumab_intravenous_composition')?.value) || 0;
    const ublituximab = Number(control.get('ublituximab_composition')?.value) || 0;
    const natalizumabIntravenous = Number(control.get('natalizumab_intravenous_composition')?.value) || 0;
    const natalizumabSubcutaneous = Number(control.get('natalizumab_subcutaneous_composition')?.value) || 0;

    const sum = rituximab + ocrelizumab + ublituximab + natalizumabIntravenous + natalizumabSubcutaneous;

    // console.log('Composition Validator - Rituximab:', rituximab, 'Ocrelizumab:', ocrelizumab, 'Ublituximab:', ublituximab, 'Sum:', sum);

    // La somma delle composizioni deve essere esattamente 100% (tolleranza per errori floating point).
    if (Math.abs(sum - 100) > 0.001) {
      // console.log('Validation FAILED - Sum must equal 100%');
      return { compositionSumExceeded: { sum, max: 100 } };
    }

    // console.log('Validation PASSED');
    return null;
  }

  ngOnInit(): void {
    // Subscribe to composition field changes to trigger the sum validation.
    for (const compositionControl of this.compositionControls) {
      this.simulatorForm.get(compositionControl)?.valueChanges.subscribe(() => {
        this.simulatorForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
      });
    }

    // Stato iniziale: tutti i campi a 0 e form disabilitato finché non si sceglie un centro.
    this.resetAllToZeroAndDisable();

    // Carica l'elenco dei centri per la combo di selezione.
    this.loadCityRecords();

    // Load simulation history
    this.loadSimulationHistory();
  }

  ngOnDestroy(): void {
    this.clearReportPoll();
  }

  /**
   * Campi non editabili: read-only in UI e/o input commentati nel template ma ancora inviati al backend.
   * Va richiamato dopo enable() perché enable() riabilita tutti i controlli.
   */
  private applyFixedFieldState(): void {
    const fixedControls = [
      'ocrelizumab_subcutaneous_min',
      'ocrelizumab_intravenous_min',
      'ublituximab_min',
      'natalizumab_intravenous_min',
      'natalizumab_subcutaneous_min',
      'Avg_Infusion_Min_Scenario',
      'Gap_TimeHours_Per_Day',
      'Working_Days_Month',
      'avg_patients_per_chair_per_day',
      'prep_time_chair_materials',
      'patient_reception_assessment_time',
      'vitals_premedication_setup_time',
      'post_infusion_surveillance_time_ocrelizumab_subcutaneous',
      'post_infusion_surveillance_time_ocrelizumab_intravenous',
      'post_infusion_surveillance_time_ublituximab',
      'post_infusion_surveillance_time_natalizumab_intravenous',
      'post_infusion_surveillance_time_natalizumab_subcutaneous',
      'post_infusion_admin_time',
      'avg_operational_gap_between_infusions',
    ];
    for (const name of fixedControls) {
      this.simulatorForm.get(name)?.disable({ emitEvent: false });
    }
  }

  /** Azzera tutti i controlli del form e disabilita l'intero form (stato di default). */
  private resetAllToZeroAndDisable(): void {
    const zeroed: Record<string, number> = {};
    Object.keys(this.simulatorForm.controls).forEach((name) => (zeroed[name] = 0));
    this.simulatorForm.reset(zeroed, { emitEvent: false });
    this.simulatorForm.disable({ emitEvent: false });
  }

  /**
   * Carica i centri di riferimento (nerf-simulator-records) per popolare la combo.
   */
  loadCityRecords(): void {
    this.loadingCities = true;
    this.backend.get('nerf-simulator-records').subscribe({
      next: (response: any) => {
        const records = response?.records ?? response?.data ?? response ?? [];
        // Ordina i centri per nome (case-insensitive) nella combo.
        this.cityRecords = [...records].sort((a: any, b: any) =>
          (a?.city ?? '').localeCompare(b?.city ?? '', undefined, { sensitivity: 'base' })
        );
        this.filteredCityRecords = [...this.cityRecords];
        this.loadingCities = false;

        // All'apertura seleziona automaticamente il centro marcato come default (select_default = 1).
        this.selectDefaultCity();
      },
      error: (error: any) => {
        console.error('Error loading nerf-simulator records:', error);
        this.cityRecords = [];
        this.loadingCities = false;
      }
    });
  }

  /**
   * Seleziona automaticamente il centro contrassegnato come default (select_default = 1)
   * e ne carica i dati nel form. Se non esiste, lascia lo stato azzerato/disabilitato.
   */
  private selectDefaultCity(): void {
    const defaultRecord = this.cityRecords.find((record: any) => Number(record?.select_default) === 1);
    if (!defaultRecord) {
      return;
    }

    // setValue con emitEvent:false aggiorna la combo senza far scattare (selectionChange).
    this.cityControl.setValue(defaultRecord.id, { emitEvent: false });
    this.loadCenter(defaultRecord);
  }

  /**
   * Cambio centro dalla combo: carica i dati del centro selezionato.
   */
  onCitySelected(recordId: number | null): void {
    const record = this.cityRecords.find((item: any) => item.id === recordId) ?? null;
    // Cambiando centro, la tabella torna a filtrare sul centro selezionato.
    this.showAllSimulations = false;
    this.pruneSelectionToDisplayed();
    this.loadCenter(record);
  }

  /** Filtra le opzioni della combo in base al testo di ricerca (case-insensitive). */
  onCitySearch(term: string): void {
    const search = (term ?? '').toLowerCase().trim();
    this.filteredCityRecords = search
      ? this.cityRecords.filter((record: any) => (record.city ?? '').toLowerCase().includes(search))
      : [...this.cityRecords];
  }

  /** All'apertura del pannello ripristina l'elenco completo e mette il focus sulla ricerca. */
  onCityPanelOpenedChange(opened: boolean): void {
    if (opened) {
      this.filteredCityRecords = [...this.cityRecords];
      // Il pannello è in un overlay: attendo il render prima di azzerare e focalizzare l'input.
      setTimeout(() => {
        const el = this.citySearchInput?.nativeElement;
        if (el) {
          el.value = '';
          el.focus();
        }
      });
    }
  }

  /**
   * Carica nel form i dati del centro selezionato, oppure ripristina lo stato
   * azzerato/disabilitato se non c'è un centro valido.
   */
  private loadCenter(record: any): void {
    // input_data può arrivare come oggetto (cast Eloquent) o come stringa JSON: gestiamo entrambi.
    let inputData: any = record?.input_data ?? null;
    if (typeof inputData === 'string') {
      try {
        inputData = JSON.parse(inputData);
      } catch {
        inputData = null;
      }
    }

    if (!record || !inputData || typeof inputData !== 'object') {
      this.resetAllToZeroAndDisable();
      return;
    }

    // Abilita il form, poi ripristina i campi fissi come read-only.
    this.simulatorForm.enable({ emitEvent: false });
    this.applyFixedFieldState();

    // Popola con i dati del centro (composizioni/Safety/No-Show da frazione a percentuale).
    this.simulatorForm.patchValue(this.toPercentageParams(inputData));

    // Reset dello stato "salvato" perché stiamo caricando nuovi parametri.
    this.simulationResult = null;
    this.hasResult = false;
    this.currentSimulationId = null;
    this.isSimulationSaved = false;
  }

  /**
   * Converte i valori memorizzati come frazione (0-1) in percentuale (0-100) per la UI.
   */
  private toPercentageParams(source: any): any {
    const params = { ...source };
    params.ocrelizumab_subcutaneous_composition = (params.ocrelizumab_subcutaneous_composition || 0) * 100;
    params.ocrelizumab_intravenous_composition = (params.ocrelizumab_intravenous_composition || 0) * 100;
    params.ublituximab_composition = (params.ublituximab_composition || 0) * 100;
    params.natalizumab_intravenous_composition = (params.natalizumab_intravenous_composition || 0) * 100;
    params.natalizumab_subcutaneous_composition = (params.natalizumab_subcutaneous_composition || 0) * 100;
    params.Safety_Buffer = (params.Safety_Buffer || 0) * 100;
    params.No_Show_Rate = (params.No_Show_Rate ?? 0) * 100;
    return params;
  }

  /** Controlli composizione (percentuali) che concorrono alla somma = 100%. */
  private readonly compositionControls: ReadonlyArray<string> = [
    'ocrelizumab_subcutaneous_composition',
    'ocrelizumab_intravenous_composition',
    'ublituximab_composition',
    'natalizumab_intravenous_composition',
    'natalizumab_subcutaneous_composition',
  ];

  onSubmit(): void {
    if (this.simulatorForm.valid) {
      this.loading = true;
      this.hasResult = false;

      // Use getRawValue() to include disabled fields
      const formData = this.simulatorForm.getRawValue();

      // Convert percentage values to decimals for backend
      formData.ocrelizumab_subcutaneous_composition = formData.ocrelizumab_subcutaneous_composition / 100;
      formData.ocrelizumab_intravenous_composition = formData.ocrelizumab_intravenous_composition / 100;
      formData.ublituximab_composition = formData.ublituximab_composition / 100;
      formData.natalizumab_intravenous_composition = formData.natalizumab_intravenous_composition / 100;
      formData.natalizumab_subcutaneous_composition = formData.natalizumab_subcutaneous_composition / 100;
      formData.Safety_Buffer = formData.Safety_Buffer / 100;
      formData.No_Show_Rate = formData.No_Show_Rate / 100;

      this.backend.post(`nerf-simulator/${this.projectId}`, formData).subscribe({
        next: (result: any) => {
          // console.log('NERF Simulator Response:', result);
          // console.log('Response type:', typeof result);

          let parsedResult = result;

          // Check if response has Lambda format (statusCode and body)
          if (result && result.statusCode && result.body) {
            // console.log('Lambda response detected, extracting body...');
            try {
              // Parse the body string
              parsedResult = JSON.parse(result.body);
              // console.log('Parsed body:', parsedResult);
            } catch (e) {
              console.error('Error parsing Lambda body:', e);
              this.backend.showErrors({ message: 'Invalid JSON in Lambda response body' });
              this.loading = false;
              return;
            }
          } else if (typeof result === 'string') {
            // Handle plain string response
            // console.log('Response is a string, parsing JSON...');
            try {
              parsedResult = JSON.parse(result);
              // console.log('Parsed result:', parsedResult);
            } catch (e) {
              console.error('Error parsing JSON:', e);
              this.backend.showErrors({ message: 'Invalid JSON response from server' });
              this.loading = false;
              return;
            }
          }

          // console.log('Final result keys:', Object.keys(parsedResult));
          // console.log('Final result:', parsedResult);
          this.simulationResult = parsedResult;
          this.hasResult = true;
          this.loading = false;
          this.isSimulationSaved = false;
          this.currentSimulationId = null; // Reset ID for new simulation
          this.currentSimulationCreatedAt = new Date().toISOString();

          // Automatically save the simulation to database
          // console.log('Auto-save triggered after simulation');
          this.saveSimulation();
        },
        error: (error: any) => {
          console.error('Error running NERF simulation:', error);
          this.backend.showErrors(error);
          this.loading = false;
        }
      });
    }
  }

  onReset(): void {
    const currentRaw = this.simulatorForm.getRawValue() as Record<string, number>;
    this.simulatorForm.reset({
      ...currentRaw,
      ...this.editableParamDefaults
    });
    this.hasResult = false;
    this.simulationResult = null;
    this.currentSimulationId = null;
    this.currentSimulationCreatedAt = null;
    this.isSimulationSaved = false;
  }

  saveSimulation(): void {
    if (!this.simulationResult) {
      this.backend.showErrors({ message: 'No simulation result to save' });
      return;
    }

    // Don't save if already saved
    if (this.isSimulationSaved) {
      // console.log('Simulation already saved, skipping save operation');
      return;
    }

    this.savingSimulation = true;

    // Convert percentage values to decimals for backend before saving
    const formData = this.simulatorForm.getRawValue();
    const inputParams = {
      ...formData,
      ocrelizumab_subcutaneous_composition: formData.ocrelizumab_subcutaneous_composition / 100,
      ocrelizumab_intravenous_composition: formData.ocrelizumab_intravenous_composition / 100,
      ublituximab_composition: formData.ublituximab_composition / 100,
      natalizumab_intravenous_composition: formData.natalizumab_intravenous_composition / 100,
      natalizumab_subcutaneous_composition: formData.natalizumab_subcutaneous_composition / 100,
      Safety_Buffer: formData.Safety_Buffer / 100,
      No_Show_Rate: formData.No_Show_Rate / 100
    };

    // Centro di riferimento selezionato (per ricaricarlo insieme ai dati).
    const selectedCityId = this.cityControl.value;
    const selectedCity = this.cityRecords.find((record: any) => record.id === selectedCityId);

    const simulationData = {
      project_id: this.projectId,
      city: selectedCity?.city ?? null,
      city_record_id: selectedCityId ?? null,
      input_parameters: inputParams,
      output_results: this.simulationResult
    };

    this.backend.post('nerf-simulations/save', simulationData).subscribe({
      next: (response: any) => {
        // console.log('Simulation saved successfully:', response);
        this.currentSimulationId = response.simulation_id || response.id;
        this.currentSimulationCreatedAt = response.created_at || response.createdAt || this.currentSimulationCreatedAt;
        this.isSimulationSaved = true;
        this.savingSimulation = false;
        this.loadSimulationHistory();
      },
      error: (error: any) => {
        console.error('Error saving simulation:', error);
        this.backend.showErrors(error);
        this.savingSimulation = false;
        this.isSimulationSaved = false;
      }
    });
  }

  loadSimulationHistory(): void {
    if (!this.projectId) {
      return;
    }

    this.loadingHistory = true;

    this.backend.get(`nerf-simulations/list/${this.projectId}`).subscribe({
      next: (response: any) => {
        // console.log('Simulation history:', response);
        this.simulationHistory = response.simulations || response.data || response;
        if (this.currentSimulationId) {
          const selectedSimulation = this.simulationHistory.find((simulation: any) => simulation.id === this.currentSimulationId);
          if (selectedSimulation?.created_at) {
            this.currentSimulationCreatedAt = selectedSimulation.created_at;
          }
        }
        this.loadingHistory = false;
      },
      error: (error: any) => {
        console.error('Error loading simulation history:', error);
        this.simulationHistory = [];
        this.loadingHistory = false;
      }
    });
  }

  // ---- Selezione simulazioni + generazione report (SurveyQuestionSummary) ----

  /**
   * Simulazioni mostrate in tabella: solo quelle del centro selezionato, a meno
   * che l'utente non abbia scelto di vederle tutte (showAllSimulations).
   */
  get displayedSimulationHistory(): any[] {
    if (this.showAllSimulations) {
      return this.simulationHistory;
    }

    const record = this.cityRecords.find((item: any) => item.id === this.cityControl.value);
    if (!record) {
      // Nessun centro selezionato: mostra tutto.
      return this.simulationHistory;
    }

    return this.simulationHistory.filter((simulation: any) =>
      (simulation.city_record_id != null && simulation.city_record_id === record.id)
      || (simulation.city != null && record.city != null && simulation.city === record.city)
    );
  }

  /** Mostra/nasconde tutte le simulazioni (ignorando il filtro per centro). */
  toggleShowAllSimulations(): void {
    this.showAllSimulations = !this.showAllSimulations;
    // Le righe ora non più visibili non devono restare selezionate.
    this.pruneSelectionToDisplayed();
  }

  /** Rimuove dalla selezione gli id non più presenti tra le righe mostrate. */
  private pruneSelectionToDisplayed(): void {
    const visibleIds = new Set(this.displayedSimulationHistory.map((simulation: any) => simulation.id));
    for (const id of Array.from(this.selectedSimulationIds)) {
      if (!visibleIds.has(id)) {
        this.selectedSimulationIds.delete(id);
      }
    }
  }

  /** True se la simulazione è spuntata. */
  isSimulationSelected(simulation: any): boolean {
    return this.selectedSimulationIds.has(simulation?.id);
  }

  /** Toggle della singola riga. */
  toggleSimulationSelection(simulation: any, checked: boolean): void {
    if (simulation?.id == null) {
      return;
    }
    if (checked) {
      this.selectedSimulationIds.add(simulation.id);
    } else {
      this.selectedSimulationIds.delete(simulation.id);
    }
  }

  /** True se tutte le righe visibili sono selezionate. */
  get allSimulationsSelected(): boolean {
    const displayed = this.displayedSimulationHistory;
    return displayed.length > 0
      && displayed.every((simulation: any) => this.selectedSimulationIds.has(simulation.id));
  }

  /** True se solo una parte è selezionata (stato indeterminato dell'header). */
  get someSimulationsSelected(): boolean {
    return this.selectedSimulationIds.size > 0 && !this.allSimulationsSelected;
  }

  /** Select/deselect all sulle righe attualmente mostrate. */
  toggleSelectAll(checked: boolean): void {
    if (checked) {
      this.displayedSimulationHistory.forEach((simulation: any) => this.selectedSimulationIds.add(simulation.id));
    } else {
      this.displayedSimulationHistory.forEach((simulation: any) => this.selectedSimulationIds.delete(simulation.id));
    }
  }

  /**
   * Avvia la generazione del report AI sulle simulazioni selezionate.
   * Il backend crea il file S3, lancia la Step Function (agente
   * SurveyQuestionSummary) e traccia lo stato; qui poi facciamo polling.
   */
  generateReport(): void {
    const simulationIds = Array.from(this.selectedSimulationIds);
    if (simulationIds.length === 0 || !this.projectId) {
      return;
    }

    this.generatingReport = true;
    this.currentReport = null;

    this.backend.post('nerf-simulations/report/generate', {
      project_id: this.projectId,
      simulation_ids: simulationIds,
    }).subscribe({
      next: (response: any) => {
        this.currentReport = response?.report ?? null;
        const reportId = response?.report_id ?? response?.report?.id ?? null;
        if (reportId) {
          this.pollReportStatus(reportId);
        } else {
          this.generatingReport = false;
        }
      },
      error: (error: any) => {
        console.error('Error starting nerf report:', error);
        this.backend.showErrors(error);
        this.generatingReport = false;
      }
    });
  }

  /** Polling dello stato del report finché non è completed/failed. */
  private pollReportStatus(reportId: number): void {
    this.clearReportPoll();
    this.reportPollTimer = setInterval(() => {
      this.backend.get(`nerf-simulations/report/${reportId}/status`).subscribe({
        next: (response: any) => {
          const report = response?.report ?? null;
          this.currentReport = report;
          const status = report?.status;
          if (status === 'completed' || status === 'failed') {
            this.clearReportPoll();
            this.generatingReport = false;
          }
        },
        error: (error: any) => {
          console.error('Error polling nerf report status:', error);
          this.clearReportPoll();
          this.generatingReport = false;
        }
      });
    }, 5000);
  }

  private clearReportPoll(): void {
    if (this.reportPollTimer) {
      clearInterval(this.reportPollTimer);
      this.reportPollTimer = null;
    }
  }

  /** Testo del report completato (report_result.text), se presente. */
  get reportResultText(): string | null {
    return this.currentReport?.report_result?.text ?? null;
  }

  /**
   * Scarica il report generato in formato Word (.doc): il markdown viene convertito in HTML
   * e incapsulato in un documento HTML compatibile con Microsoft Word.
   */
  downloadReportWord(): void {
    const text = this.reportResultText;
    if (!text) {
      this.backend.showErrors({ message: 'No report to download' });
      return;
    }

    const bodyHtml = marked.parse(text, { async: false }) as string;
    const title = 'NERF Simulator - Report';
    const html =
      '<html xmlns:o="urn:schemas-microsoft-com:office:office" ' +
      'xmlns:w="urn:schemas-microsoft-com:office:word" ' +
      'xmlns="http://www.w3.org/TR/REC-html40">' +
      '<head><meta charset="utf-8"><title>' + title + '</title>' +
      '<style>body{font-family:Calibri,Arial,sans-serif;font-size:11pt;} table{border-collapse:collapse;} ' +
      'td,th{border:1px solid #999;padding:4px;}</style></head>' +
      '<body>' + bodyHtml + '</body></html>';

    const blob = new Blob(['﻿', html], { type: 'application/msword' });
    const reportId = this.currentReport?.id ?? 'current';
    saveAs(blob, `nerf-report-${reportId}.doc`);
  }

  loadSimulation(simulation: any): void {
    if (!simulation || !simulation.input_parameters) {
      return;
    }

    // Enable the form (default state is disabled until a selection is made), then keep
    // the fixed fields read-only.
    this.simulatorForm.enable({ emitEvent: false });
    this.applyFixedFieldState();

    // Convert decimal values from backend to percentages for display and load into the form.
    this.simulatorForm.patchValue(this.toPercentageParams(simulation.input_parameters));

    // Ripristina il centro di riferimento nella combo (senza emettere selectionChange,
    // per non ricaricare i dati originali del centro sopra quelli salvati).
    this.cityControl.setValue(simulation.city_record_id ?? null, { emitEvent: false });

    // Load results
    this.simulationResult = simulation.output_results;
    this.hasResult = true;
    this.currentSimulationId = simulation.id;
    this.currentSimulationCreatedAt = simulation.created_at || null;
    this.isSimulationSaved = true; // This simulation is already saved

    // Scroll al titolo risultati (accordion; l'ex .results-main-card non esiste più)
    setTimeout(() => {
      document.getElementById('nerf-simulation-results-anchor')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 150);
  }

  exportToExcel(simulationId?: number): void {
    const idToExport = simulationId || this.currentSimulationId;

    if (!idToExport) {
      this.backend.showErrors({ message: 'No simulation to export' });
      return;
    }

    // console.log('Starting Excel export for simulation ID:', idToExport);

    // Use getBlob method from BackendService
    this.backend.getBlob(`nerf-simulations/export/${idToExport}`).subscribe({
      next: (response: any) => {
        // console.log('Excel export response received:', response);

        // Extract the blob from the response
        const blob = response.body || response;

        // Get filename from Content-Disposition header or use default
        let filename = `nerf-simulation-${idToExport}.xlsx`;
        const contentDisposition = response.headers?.get('Content-Disposition');
        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        // Create a download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // console.log('Excel file downloaded successfully');
      },
      error: (error: any) => {
        console.error('Error downloading Excel file from backend:', error);
        console.error('Error status:', error?.status);
        console.error('Error message:', error?.message);

        // Fallback to CSV export if backend fails
        // console.log('Attempting CSV export as fallback...');
        this.exportToCSV(idToExport);
      }
    });
  }

  exportToCSV(simulationId?: number): void {
    // Find the simulation data
    let simulation: any;

    if (simulationId) {
      simulation = this.simulationHistory.find(s => s.id === simulationId);
    } else if (this.currentSimulationId) {
      simulation = this.simulationHistory.find(s => s.id === this.currentSimulationId);
    }

    // If not found in history, use current result
    if (!simulation && this.simulationResult) {
      const selectedCity = this.cityRecords.find((record: any) => record.id === this.cityControl.value);
      simulation = {
        id: this.currentSimulationId || 'current',
        city: selectedCity?.city ?? null,
        input_parameters: this.simulatorForm.getRawValue(),
        output_results: this.simulationResult,
        created_at: new Date().toISOString()
      };
    }

    if (!simulation) {
      this.backend.showErrors({ message: 'No simulation data to export' });
      return;
    }

    // Create CSV content
    let csvContent = 'NERF Simulator - Simulation Results\n\n';
    csvContent += `Simulation ID:,${simulation.id}\n`;
    csvContent += `Center:,${simulation.city || '-'}\n`;
    csvContent += `Date:,${new Date(simulation.created_at).toLocaleString()}\n\n`;

    // Input Parameters
    csvContent += 'INPUT PARAMETERS\n';
    csvContent += 'Parameter Name,Value,Unit\n';

    const inputParams = simulation.input_parameters;
    const paramUnits: any = {
      'ocrelizumab_subcutaneous_min': 'min',
      'ocrelizumab_subcutaneous_composition': '%',
      'ocrelizumab_intravenous_min': 'min',
      'ocrelizumab_intravenous_composition': '%',
      'ublituximab_min': 'min',
      'ublituximab_composition': '%',
      'natalizumab_intravenous_min': 'min',
      'natalizumab_subcutaneous_min': 'min',
      'natalizumab_intravenous_composition': '%',
      'natalizumab_subcutaneous_composition': '%',
      'Hours_Per_Day': 'h',
      'Gap_Min': 'min',
      'Working_Days_Month': 'days',
      'Avg_Infusion_Min_Scenario': 'min',
      'Safety_Buffer': '%',
      'No_Show_Rate': '%',
      'Gap_TimeHours_Per_Day': 'h',
      'Nurses_per_Shift': '',
      'Patients_per_Nurse': '',
      'avg_patients_per_chair_per_day': '',
      'Chairs_Active': '',
      'number_of_opening_days': 'days',
      'prep_time_chair_materials': 'min',
      'patient_reception_assessment_time': 'min',
      'vitals_premedication_setup_time': 'min',
      'post_infusion_admin_time': 'min',
      'avg_operational_gap_between_infusions': 'min',
      'infusion_service_hours': 'h'
    };

    for (const key in inputParams) {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      csvContent += `${label},${inputParams[key]},${paramUnits[key] || ''}\n`;
    }

    // Output Results
    csvContent += '\nOUTPUT RESULTS\n';
    csvContent += 'Result Name,Value,Unit\n';

    const outputResults = simulation.output_results;
    const resultUnits: any = {
      'utilization_percent': '%',
      'patients_per_month': 'patients',
      'cycles_per_chair_day': 'cycles',
      'rotation_index': '',
      'effective_chairs': 'chairs',
      'nurse_capacity': '',
      'avg_infusion_time_mix': 'min'
    };

    for (const key in outputResults) {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const value = typeof outputResults[key] === 'number' ? outputResults[key].toFixed(4) : outputResults[key];
      csvContent += `${label},${value},${resultUnits[key] || ''}\n`;
    }

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `nerf-simulation-${simulation.id}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // console.log('CSV export completed successfully');
  }

  handleDeleteClick(event: Event, simulationId: number): void {
    // Prevent all event propagation
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // console.log('=== DELETE BUTTON CLICKED ===');
    // console.log('Event prevented and stopped');

    // Call the actual delete function
    this.deleteSimulation(simulationId);
  }

  deleteSimulation(simulationId: number): void {
    if (!confirm('Are you sure you want to delete this simulation?')) {
      // console.log('Delete cancelled by user');
      return;
    }

    // console.log('=== DELETE OPERATION START ===');
    // console.log('Attempting to delete simulation ID:', simulationId);
    // console.log('Delete endpoint URL:', `nerf-simulations/${simulationId}`);
    // console.log('Using backend.destroy() method which sends HTTP DELETE');

    // Use destroy() method which actually does HTTP DELETE
    this.backend.destroy(`nerf-simulations/${simulationId}`).subscribe({
      next: (response: any) => {
        // console.log('=== DELETE SUCCESS ===');
        // console.log('Simulation deleted successfully:', response);

        // Remove from local history array immediately for better UX
        this.simulationHistory = this.simulationHistory.filter(s => s.id !== simulationId);
        // console.log('Removed from local array. New history count:', this.simulationHistory.length);

        // Clear current simulation if it was deleted
        if (this.currentSimulationId === simulationId) {
          // console.log('Clearing currently displayed simulation');
          this.currentSimulationId = null;
          this.currentSimulationCreatedAt = null;
          this.hasResult = false;
          this.simulationResult = null;
          this.isSimulationSaved = false;
        }

        // Reload history from server to sync
        // console.log('Reloading simulation history from server...');
        this.loadSimulationHistory();
      },
      error: (error: any) => {
        console.error('Error deleting simulation:', error);
        console.error('Error status:', error?.status);
        console.error('Error message:', error?.message);
        console.error('Full error object:', error);

        // Check if it's a 404 or backend not implemented
        if (error?.status === 404) {
          alert('Delete endpoint not found. The backend endpoint may not be implemented yet.\n\nEndpoint: DELETE /api/nerf-simulations/' + simulationId);
        } else if (error?.status === 0) {
          alert('Cannot connect to backend. Please check if the Laravel server is running.');
        } else {
          this.backend.showErrors(error);
        }
      }
    });
  }

}
